import { CalendarProps, Calendar, Flex, Select, Badge, BadgeProps } from 'antd';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// import dayLocaleData from 'dayjs/plugin/localeData';
import React, { useEffect, useState } from 'react';
import { useNotificationContext } from '../../../../contexts/NotificationContext';
import { getMonthlyData } from '../../getData';
import { TaskStatus } from '../../timelineItem';
import { useTranslation } from 'react-i18next';
import BasicCard from '../../../../components/basicCard/BasicCard';
import RESOURCE_CONFIGS from '../../../../constants/resourceConfig';
import { useMediaQuery } from 'react-responsive';
import { IsOnlineContext, useIsOnlineContext } from '../../../../contexts/IsOnlineContext';
import './MonthlyView.scss';

// dayjs.extend(dayLocaleData);
dayjs.extend(localizedFormat);

const MonthlyView = () => {
  const { t } = useTranslation('inspector');

  const [selectedMonthStartAndEnd, setSelectedMonthStartAndEnd] = useState<{
    startDate: number;
    endDate: number;
  }>({
    startDate: dayjs().startOf('month').valueOf(),
    endDate: dayjs().endOf('month').valueOf(),
  });

  const { errorNotification } = useNotificationContext();

  const [monthlyData, setMonthlyData] = useState<any>();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const { isOnline } = useIsOnlineContext();

  const onPanelChange = (value: Dayjs, mode: CalendarProps<Dayjs>['mode']) => {
    setSelectedMonthStartAndEnd({
      startDate: value.startOf('month').valueOf(),
      endDate: value.endOf('month').valueOf(),
    });
  };

  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  useEffect(() => {
    const getMonthlyDataWrapper = async () => {
      try {
        const data: { [key: number]: any[] } = await getMonthlyData(
          selectedMonthStartAndEnd.startDate,
          selectedMonthStartAndEnd.endDate
        );

        const dataWithNewKeys: any = {};
        if (data) {
          Object.keys(data).forEach((key, _index) => {
            const newKey = dayjs(Number(key)).format('D');

            dataWithNewKeys[newKey] = data[Number(key)];
          });
        }

        setMonthlyData(dataWithNewKeys);
      } catch (error: any) {
        if (isOnline) {
          console.error('Error fetching monthly data:', error);
          errorNotification(error.message);
        }
      }
    };

    getMonthlyDataWrapper();
  }, [selectedMonthStartAndEnd]);

  const getDayData = (value: Dayjs) => {
    if (monthlyData) {
      const tasks = monthlyData[value.date()];

      let dayData: BadgeProps[] = [];
      let pendingTaskCount = 0;
      let delayedTaskCount = 0;
      let completedTaskCount = 0;
      let cancelledTaskCount = 0;
      let inprogressTaskCount = 0;
      if (tasks) {
        tasks.forEach((task: any) => {
          if (task.status === TaskStatus.COMPLETED) {
            completedTaskCount += 1;
          } else if (task.status === TaskStatus.CANCELLED) {
            cancelledTaskCount += 1;
          } else if (task.status === TaskStatus.DELAYED) {
            delayedTaskCount += 1;
          } else if (task.status === TaskStatus.PENDING) {
            pendingTaskCount += 1;
          } else if (task.status === TaskStatus.IN_PROGRESS) {
            inprogressTaskCount += 1;
          }
        });

        if (pendingTaskCount > 0) {
          dayData.push({
            status: 'warning',
            content: t('pendingTaskCount', { count: pendingTaskCount }),
          });
        }
        if (completedTaskCount > 0) {
          dayData.push({
            status: 'success',
            content: t('completedTaskCount', { count: completedTaskCount }),
          });
        }
        if (cancelledTaskCount > 0) {
          dayData.push({
            status: 'default',
            content: t('cancelledTaskCount', { count: cancelledTaskCount }),
          });
        }
        if (delayedTaskCount > 0) {
          dayData.push({
            status: 'error',
            content: t('delayedTaskCount', { count: delayedTaskCount }),
          });
        }
        if (inprogressTaskCount > 0) {
          dayData.push({
            status: 'processing',
            content: t('inProgressTaskCount', { count: inprogressTaskCount }),
          });
        }

        return dayData;
      }
    }
  };

  // const monthCellRender = (value: Dayjs) => {
  //   const num = getMonthData(value);
  //   return num ? (
  //     <div className="notes-month">
  //       <section>{num}</section>
  //       <span>Backlog number</span>
  //     </div>
  //   ) : null;
  // };

  const dateCellRender = (value: Dayjs) => {
    const dayData = getDayData(value);
    if (!dayData) return null;
    if (dayData) {
      if (isMobile) {
        return (
          <div className="flex flex-wrap gap-1">
            {dayData.map((item) => (
              <Badge
                key={item.status + String(item.content)}
                status={item.status as BadgeProps['status']}
              />
            ))}
          </div>
        );
      }
      return (
        <ul className="space-y-2">
          {dayData.map((item) => (
            <li key={item.content}>
              <Badge status={item.status as BadgeProps['status']} text={item.content} />
            </li>
          ))}
        </ul>
      );
    }
    return null;
  };

  const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
    if (info.type === 'date') return dateCellRender(current);
    return info.originNode;
  };

  const selectedDayData = selectedDate ? getDayData(selectedDate) : undefined;

  return (
    <div className="mb-6 mt-16">
      <BasicCard>
        <div className="p-6">
          <Calendar
            className="monthly-calendar-monitoring-vists"
            onPanelChange={onPanelChange}
            cellRender={cellRender}
            onSelect={(date) => setSelectedDate(date)}
            headerRender={({ value, type, onChange, onTypeChange }) => {
              const monthFormat = 'MMMM';
              const year = value.year();
              // const month = value.month();
              const month = value.format(monthFormat);

              const yearOptions = Array.from({ length: 20 }, (_, i) => {
                const label = year - 10 + i;
                return { label, value: label };
              });

              const monthOptions = Array.from({ length: 12 }, (_, index) => ({
                label: dayjs().month(index).format('MMM'), // short month name
                value: index,
              }));

              return (
                <div className="mb-8 p-2">
                  {/* <Typography.Title level={4}>Custom header</Typography.Title> */}
                  <Flex
                    gap={8}
                    justify="flex-end"
                    className="flex-col items-stretch md:flex-row md:items-center md:justify-end"
                  >
                    {/* <Radio.Group size="small" onChange={(e) => onTypeChange(e.target.value)} value={type}>
                <Radio.Button value="month">Month</Radio.Button>
                <Radio.Button value="year">Year</Radio.Button>
              </Radio.Group> */}
                    <Select
                      size="large"
                      popupMatchSelectWidth={false}
                      value={year}
                      className="w-full md:w-32"
                      options={yearOptions}
                      onChange={(newYear) => {
                        const now = value.clone().year(newYear);
                        onChange(now);
                      }}
                    />
                    <Select
                      size="large"
                      popupMatchSelectWidth={false}
                      value={month}
                      className="w-full md:w-32"
                      options={monthOptions}
                      onChange={(newMonth) => {
                        const now = value.clone().month(Number(newMonth));
                        onChange(now);
                      }}
                    />
                  </Flex>
                </div>
              );
            }}
          />
          {isMobile && selectedDayData && (
            <div className="mt-4 border-t pt-3">
              <h4 className="mb-2 text-sm font-semibold">
                {t('tasksOnDate', { date: selectedDate?.format('LL') })}
              </h4>
              <ul className="space-y-1 text-xs">
                {selectedDayData.map((item) => (
                  <li key={item.content} className="flex items-center">
                    <Badge status={item.status as BadgeProps['status']} />
                    <span className="ml-2">{item.content}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </BasicCard>
    </div>
  );
};

export default MonthlyView;
